/**
 * Checks url for orderUuid and attempts to fetch order details
 * if orderUuid is not found or order does not exist, redirects to order lookup page
 */
const getOrderDetails = async () => {
  const params = new URLSearchParams(window.location.search);
  const orderUuidParam = params.get('orderUuid');
  if (orderUuidParam) {
    const order = await fetchOrderDetails(orderUuidParam);
    if (order) {
      renderOrderStatus(order);
    }
  } else if (!Shopify.designMode) {
    window.location = `${Shopify.routes.root}pages/order-lookup`;
  }
}

/**
 * fetches order details from the server
 * @param {*} orderUuid 
 * @returns {object} order details
 */
const fetchOrderDetails = async (orderUuid) => {
  const query = `
    query getOrderDetails(
      $orderUuid: String!
    ) {
      order(orderUuid: $orderUuid) {
        id
        uuid
        uuidPrefix
        state
        orderCapturedAt
        orderPlacedAt
        amount
        salesTax
        shipping
        total
        commercePlatform
        externalOrderNumber
        trackingNumber
        returnableUntil
        isLocked
        canBeCanceled
        canceledAt
        email
        postalCode
        orderItems {
          returnInitiatedAt
          returnInitiatedFrom
          returnedAt
          saleDisplayPrice
          salePrice
          saleNotes
          ypackageId
          yPackage {
            carrier
            trackingNumber
            state
          }
          inventoryItem {
            uuid
            notes
            price
            state
            condition {
              code
              translatedDisplayName
            }
            color {
              code
              displayName
            }
            size {
              code
              displayName
            }
            images {
              tag
              url
            }
            catalogItem {
              title
              parentSku
              price
            }
          }
        }
      }
    }
  `;
  const param = 'query-orderDetails';
  try {
    const response = await fetchWithAppName(`${global.url}${param}`, {
      body: JSON.stringify({ query, variables: { orderUuid } }),
    });
  
    const jsonData = await response.json();
    if (jsonData.errors) {
      throw new Error(
        `Failed to fetch order details. API returned GraphQL errors. [orderUuid=${
          orderUuid
        }, errors=${JSON.stringify(jsonData.errors)}]`,
      );
    }
    if (!jsonData.data?.order) {
      throw new Error(
        `Failed to fetch order details. API unexpectedly didn't return an order. [orderUuid=${
          orderUuid
        }]`,
      );
    }

    // return jsonData.data.order;

    // Annoyingly rename condition.translatedDisplayName to condition.displayName so downstream code
    // can keep using condition.displayName. Hopefully this is temporary and can be reverted once we
    // implement https://troverecommerce.atlassian.net/browse/ENG-24170.
    return {
      ...jsonData.data.order,
      orderItems: jsonData.data.order.orderItems?.map(orderItem => ({
        ...orderItem,
        inventoryItem: orderItem.inventoryItem && {
          ...orderItem.inventoryItem,
          condition: {
            ...orderItem.inventoryItem.condition,
            displayName: orderItem.inventoryItem.condition.translatedDisplayName,
            translatedDisplayName: undefined,
          },
        },
      })),
    };
  } catch (e) {
    console.error(e);
  }
};

/**
 * renders order details to the page
 * @param {object} order 
 */
async function renderOrderStatus(order) {
  const orderNumber = order.commercePlatform === 'shopify' && order.externalOrderNumber ?
    order.externalOrderNumber.toUpperCase() :
    order.uuidPrefix;

  document.querySelector('.order-number').textContent = orderNumber;
  document.querySelector('.order-placed-date').textContent = `${ORDER_STATUS_CONFIG.orderDatePlacedText} ${
    DATE_TIME_FORMATS.PADDED_NUMERIC_DATE.format(new Date(order.orderPlacedAt))
  }`;
  const orderItemList = document.querySelector('order-item-list');
  orderItemList.renderOrderItems(order);
  renderTotals(order);
  renderOrderActions(order);
  renderCanceledOrderDescription(order);
}

/**
 * Renders order totals
 * @param {object} order 
 */
function renderTotals(order) {
  const subtotalEl = document.querySelector('.order-subtotal');
  const taxEl = document.querySelector('.order-tax');
  const shippingEl = document.querySelector('.order-shipping');
  const totalEl = document.querySelector('.order-total');
  const { amount, salesTax, shipping, total } = order;
  subtotalEl.textContent = formatCurrency(amount);
  taxEl.textContent = formatCurrency(salesTax);
  shippingEl.textContent = formatCurrency(shipping);
  totalEl.textContent = formatCurrency(total);
}

function getLoopReturnsUrl(baseUrl, order) {
  try {
    const url = new URL(baseUrl);
    url.searchParams.append('order', order.externalOrderNumber || '');
    url.searchParams.append('zip', order.postalCode || '');
    return url.toString();
  } catch (err) {
    console.error(
      `Error adding query params to Loop Returns URL. Linking without params. [baseUrl=${
        baseUrl
      }]`,
      err,
    );
    return baseUrl;
  }
}

function getOrderReturnsUrl(order) {
  if (order.commercePlatform === 'shopify') {
    // For now, we assume all Shopify partners use Loop Returns for returns.
    return getLoopReturnsUrl(ORDER_STATUS_CONFIG.orderStatusActions.shopifyReturnsUrl, order);
  }
  // For now, we do no pre-filling of params for legacy returns.
  return ORDER_STATUS_CONFIG.orderStatusActions.legacyReturnsUrl;
}

/**
 * Renders order actions
 * @param {object} order
 */
function renderOrderActions(order) {
  const {
    canBeCanceled,
    isLocked,
    returnableUntil,
  } = order;

  const { getOrderStatus } = ORDER_UTILS;
  const status = getOrderStatus(order);

  const cancelOrderAction = {
    enabledVersion: document.querySelector('.order-actions__cancel-order-action--enabled'),
    disabledVersion: document.querySelector('.order-actions__cancel-order-action--disabled'),
  };
  const initiateReturnAction = {
    enabledVersion: document.querySelector('.order-actions__initiate-return-action--enabled'),
    disabledVersion: document.querySelector('.order-actions__initiate-return-action--disabled'),
  };
  const initiateTradeInAction = document.querySelector('.order-actions__initiate-trade-in-action');

  const allActions = [
    cancelOrderAction.enabledVersion,
    cancelOrderAction.disabledVersion,
    initiateReturnAction.enabledVersion,
    initiateReturnAction.disabledVersion,
    initiateTradeInAction,
  ];

  if (status === 'CANCELED') {
    allActions.forEach(action => action?.classList.add('hidden'));
  } else if (['SHIPPED', 'DELIVERED'].includes(status)) {
    let actionToShow;
    if (Date.now() < new Date(returnableUntil) || returnableUntil == null) {
      actionToShow = initiateReturnAction.enabledVersion;
      if (actionToShow) {
        const initiateReturnButton = actionToShow.querySelector(
          '.order-actions__initiate-return-button',
        );
        initiateReturnButton.href = getOrderReturnsUrl(order);
      }
    } else if (ORDER_STATUS_CONFIG.orderStatusActions.shouldUseInitiateTradeInAction) {
      actionToShow = initiateTradeInAction;
    } else {
      actionToShow = initiateReturnAction.disabledVersion;
    }
    allActions.forEach((action) => {
      action?.classList.toggle('hidden', action !== actionToShow);
    });
  } else {
    const actionToShow = canBeCanceled && !isLocked ?
      cancelOrderAction.enabledVersion :
      cancelOrderAction.disabledVersion;
    allActions.forEach((action) => {
      action?.classList.toggle('hidden', action !== actionToShow);
    });
  }
}

/**
 * Renders or hides a message about canceled orders
 * @param {object} order 
 */
function renderCanceledOrderDescription(order) {
  const { getOrderStatus } = ORDER_UTILS;
  const status = getOrderStatus(order);
  const canceledOrderDescription = document.querySelector('.order-status__canceled-order-description');

  if (status === 'CANCELED') {
    const cancelationDateText = DATE_TIME_FORMATS.PADDED_NUMERIC_DATE.format(new Date(
      order.canceledAt,
    ));
    canceledOrderDescription.querySelectorAll('.cancelation-date').forEach((el) => {
      el.textContent = cancelationDateText;
    });
    canceledOrderDescription.classList.remove('hidden');
  } else {
    canceledOrderDescription.classList.add('hidden');
  }
}

getOrderDetails();

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelector('.order-status__back-link')
    .classList[
      ORDER_TRADE_IN_LOOKUP_TOKEN_UTILS.getActiveToken() ? 'remove' : 'add'
    ]('hidden');

  document
    .querySelector(
      '.order-actions__cancel-order-action--enabled .order-actions__cancel-order-button',
    )
    .addEventListener('click', () => {
      CANCEL_ORDER_MODAL.openModal();
    });
});
